import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCol,
    IonContent,
    IonIcon,
    IonPage,
    IonRow
} from "@ionic/react";
import React, { useEffect, useState } from "react";
import { Competition, Person } from "../../../../../../models";
import Header from "../../../../../../components/Headers/Header";
import PageTitle from "../../../../../../components/PageTitle/PageTitle";
import { deleteCompetition, getAllCompetitions, updateCompetition } from "../../../../../../utilities/competition/Competition";
import Spinner from "../../../../../../components/Spinners/Spinner";
import { Table } from "reactstrap";
import { formatStartEndDatesStrings } from "../../../../../../utilities/dates/FormatDates";
import { mailSharp, pencilSharp, trashSharp } from "ionicons/icons";
import { generateAELCompetitionInvitationReport } from "../../../../../../utilities/reports/CompetitionReport";
import { formatShortAddress } from "../../../../../../utilities/address/FormatAddress";
import moment from "moment";
import { DeleteCompetitionInput, UpdateCompetitionInput } from "../../../../../../API";
import { useHistory } from "react-router-dom";
import Select from 'react-select';
import BasicModal from "../../../../../../components/Modals/BasicModal";
import ErrorBanner from "../../../../../../components/Banners/ErrorBanner";
import { getPersonByPersonId } from "../../../../../../utilities/person/Person";
import { formatDisplayNameForUser } from "../../../../../../utilities/person/PersonNameFormat";
import { sendApprovedCompetitionEmail, sendStatusChangedCompetitionEmail } from "../../../../../../utilities/emails/CompetitionEmail";

interface optionInterface {
    label: string;
    value: string;
}

const statusOptions: optionInterface[] = [
    { label: "Draft", value: "draft" },
    { label: "Published", value: "published" },
    { label: "Cancelled", value: "cancelled" }
];

interface FormattedCompetition {
    id: string;
    startDate: string;
    name: string;
    address: string;
    createdBy: string;
    updatedOn: string;
    status?: string;
    competition: Competition;
}

const AdminReviewCompetitionsPage: React.FC = () => {
    const history = useHistory();

    const [competitions, setCompetitions] = useState<Competition[]>([]);
    const [formattedCompetitions, setFormattedCompetitions] = useState<FormattedCompetition[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedCompetition, setSelectedCompetition] = useState<Competition | null>(null);
    
    useEffect(() => {
        getCompetitions();
    }, []);

    const getCompetitions = async () => {
        setIsLoading(true);
        setError("");
        const competitionQuery = await getAllCompetitions();
        if (competitionQuery.isSuccess) {
            const list: Competition[] = competitionQuery.result;
            setCompetitions(list);
            await formatCompetitions(list);
        }
        setIsLoading(false);
    }

    const formatCompetitions = async (currentCompetitions: Competition[]) => {
        const formattedCompetitions: FormattedCompetition[] = [];
        for (let i = 0; i < currentCompetitions.length; i++) {
            const currentCompetition = currentCompetitions[i];
            const formattedCompetition: FormattedCompetition = {
                id: currentCompetition.id,
                address: currentCompetition.address ? formatShortAddress(currentCompetition.address) : "",
                startDate: currentCompetition.startDate ? formatStartEndDatesStrings(currentCompetition.startDate) : "",
                name: currentCompetition.name,
                createdBy: currentCompetition.createdBy ? await getCreatedBy(currentCompetition.createdBy) : "",
                status: currentCompetition.status ? currentCompetition.status : undefined,
                updatedOn: currentCompetition.updatedOn ? moment(currentCompetition.updatedOn).format("MM/DD/YY hh:mm a") : "",
                competition: currentCompetition
            };

            // Third, add the formatted competition to the list
            formattedCompetitions.push(formattedCompetition);
        }
        setFormattedCompetitions(formattedCompetitions);
    }

    const getCreatedBy = async (createdBy: string) => {
        const personQuery = await getPersonByPersonId(createdBy);
        if (personQuery.isSuccess) {
            const person: Person = personQuery.result;
            const personName = formatDisplayNameForUser(person);
            return personName;
        }
        return "";
    }

    const createCompetitionInvitation = async (selectedCompetition: Competition) => {
        if (selectedCompetition) {
            setIsLoading(true); 
            await generateAELCompetitionInvitationReport(selectedCompetition, true);
            setIsLoading(false);
        }
        else setError("No competition found. Cannot generate report.");
    }

    const handleEditCompetitionClick = async (selectedCompetition: Competition) => {
        if (selectedCompetition) {
            const path = `/index/ael/event/edit/${selectedCompetition.id}`;
            history.push(path);
        }
        else setError("No competition found.");
    }

    const updateCompetitionStatus = async (selectedComp: Competition, index: number, selectedStatus: optionInterface) => {
        const updateInput: UpdateCompetitionInput = {
            id: selectedComp.id,
            status: selectedStatus.value
        };
        const updateResult = await updateCompetition(updateInput);
        if (updateResult.isSuccess) {
            const updatedCompetition = updateResult.result;
            const updatedCompList = [
                ...competitions.slice(0, index),
                updatedCompetition,
                ...competitions.slice(index+1)
            ];
            setCompetitions(updatedCompList);
            const updatedFormattedCompList = formattedCompetitions.map(fc => {
                if (fc.id === updatedCompetition.id) {
                    return { ...fc, status: updatedCompetition.status };
                }
                return fc;
            });
            setFormattedCompetitions(updatedFormattedCompList);

            // Send the approved competition email to the creator
            const createdBy = selectedComp.createdBy;
            const personQuery = await getPersonByPersonId(createdBy);
            if (personQuery.isSuccess) {
                const person: Person = personQuery.result;
                const creatorEmail = person.email;
                const competitionDateDisplayString = selectedComp.startDate ? moment(selectedComp.startDate).format("MM-DD-YYYY") : null;
                const pdf = await generateAELCompetitionInvitationReport(selectedComp, false);
                if (pdf) {
                    if (selectedStatus.value === "published") await sendApprovedCompetitionEmail(selectedComp.name, competitionDateDisplayString || "No date found.", "Approved", creatorEmail, pdf);
                    else await sendStatusChangedCompetitionEmail(selectedComp.name, competitionDateDisplayString || "No date found.", selectedStatus.label, creatorEmail, pdf);
                }
            }
        } else {
            setError("Sorry, an error occurred. The competition's status could not be updated.");
        }
    }

    const handleSelectedCompetitionForDelete = (selectedComp: Competition) => {
        setSelectedCompetition(selectedComp);
        setShowDeleteModal(true);
    }

    const handleDeleteCompetition = async () => {
        if (selectedCompetition) {
            const deleteInput: DeleteCompetitionInput = {
                id: selectedCompetition.id
            };
            const deleteResult = await deleteCompetition(deleteInput);
            if (deleteResult.isSuccess) {
                const deletedCompList = competitions.filter(c => c.id !== selectedCompetition.id);
                const deletedFormattedCompList = formattedCompetitions.filter(fc => fc.id !== selectedCompetition.id);
                setCompetitions(deletedCompList);
                setFormattedCompetitions(deletedFormattedCompList);
            } else {
                setError("Sorry, an error occurred. The competition could not be deleted.");
            }
        } else {
            setError("No competition found. Cannot delete.");
        }
        setShowDeleteModal(false);
        setSelectedCompetition(null);
    }

    return (
        <IonPage className="bg-light">
            <Header />
            <IonContent>
                <PageTitle title="Review Competitions" />
                <IonRow className="ion-justify-content-center">
                    <IonCol size="12">
                        <IonCard mode="md" className="ion-padding bg-white">
                            {error && <ErrorBanner error={error} />}
                            {isLoading ?
                                <Spinner />
                                :
                                <IonCardContent>
                                    {(formattedCompetitions && formattedCompetitions.length > 0) ?
                                        <Table responsive striped hover>
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <p className="ion-text-wrap">Comp Date</p>
                                                    </th>
                                                    <th>
                                                        <p className="ion-text-wrap">Comp Name</p>
                                                    </th>
                                                    <th>
                                                        <p className="ion-text-wrap">Location</p>
                                                    </th>
                                                    <th>
                                                        <p className="ion-text-wrap">Created By</p>
                                                    </th>
                                                    <th>
                                                        <p className="ion-text-wrap">Last Updated</p>
                                                    </th>
                                                    <th>
                                                        <p className="ion-text-wrap">Invitation</p>
                                                    </th>
                                                    <th>
                                                        <p className="ion-text-wrap">Edit</p>
                                                    </th>
                                                    <th>
                                                        <p className="ion-text-wrap">Status</p>
                                                    </th>
                                                    <th>
                                                        <p className="ion-text-wrap">Delete</p>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formattedCompetitions.map((formattedCompetition, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <p className="ion-text-wrap">{formattedCompetition.startDate}</p>
                                                        </td>
                                                        <td>
                                                            <p className="ion-text-wrap">{formattedCompetition.name}</p>
                                                        </td>
                                                        <td>
                                                            <p className="ion-text-wrap">{formattedCompetition.address}</p>
                                                        </td>
                                                        <td>
                                                            <p className="ion-text-wrap">{formattedCompetition.createdBy}</p>
                                                        </td>
                                                        <td>
                                                            <p className="ion-text-wrap">{formattedCompetition.updatedOn}</p>
                                                        </td>
                                                        <td>
                                                            <IonButton color="tertiary" onClick={() => createCompetitionInvitation(formattedCompetition.competition)}>
                                                                <p><IonIcon icon={mailSharp}/> View</p>
                                                            </IonButton>
                                                        </td>
                                                        <td>
                                                            <IonButton color="primary" onClick={() => handleEditCompetitionClick(formattedCompetition.competition)}>
                                                                <p><IonIcon icon={pencilSharp}/> Edit</p>
                                                            </IonButton>
                                                        </td>
                                                        <td className="extra-large-input-width">
                                                            <Select
                                                                id="select-dropdown-component-input"
                                                                styles={{
                                                                    // Fixes the overlapping problem of the component
                                                                    menu: provided => ({ ...provided, zIndex: 9999 })
                                                                }}
                                                                value={statusOptions.find(option => option.value === formattedCompetition.status)}
                                                                menuPortalTarget={document.body}
                                                                isClearable={false}
                                                                isSearchable
                                                                options={statusOptions}
                                                                onChange={(event: any) => updateCompetitionStatus(formattedCompetition.competition, index, event)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <IonButton color="danger" onClick={() => handleSelectedCompetitionForDelete(formattedCompetition.competition)}>
                                                                <p><IonIcon icon={trashSharp}/> Delete</p>
                                                            </IonButton>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                        :
                                        <p>No competitions were found.</p>
                                    }
                                </IonCardContent>
                            }
                        </IonCard>
                    </IonCol>
                </IonRow>
                <BasicModal 
                    title="Delete Competition"
                    isOpen={showDeleteModal}
                    onClose={() => {setShowDeleteModal(false); setSelectedCompetition(null);}}
                >
                    <IonRow className="ion-text-center">
                        <IonCol size="12">
                            <p>Are you sure you want to delete the following competition?</p>
                            <p>{selectedCompetition?.name}</p>
                        </IonCol>
                        <IonCol size="6">
                            <IonButton color="light" onClick={() => {setShowDeleteModal(false); setSelectedCompetition(null);}}>
                                Cancel
                            </IonButton>
                        </IonCol>
                        <IonCol size="6">
                            <IonButton color="danger" onClick={() => handleDeleteCompetition()}>
                                Delete
                            </IonButton>
                        </IonCol>
                    </IonRow>
                </BasicModal>
            </IonContent>
        </IonPage>
    );
};

export default AdminReviewCompetitionsPage;